import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import MoreVertOutlined from "@mui/icons-material/MoreVertOutlined";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";

import DeleteConfirmationButton from "../../../mui-toolkit/DeleteConfirmationButton";
import { formatProducerName } from "../../../utility/formatters";
import { ArrowDownIcon } from "../../Icons/Icons";
import PaginationControls from "./PaginationControls";

const ACTION_BUTTON_SX = { minWidth: 48, minHeight: 48 };

function ResponsiveDataGridCard({ row, index, columns, getCardStyles, renderCellValue, renderActionButtons }) {
  const [expanded, setExpanded] = useState(true);
  const actionPopupId = useId();
  const hasSubdata = Array.isArray(row.subdata) && row.subdata.length > 0;
  const HEADER_WIDTH = 130;

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };
  const renderRow = (rowData, isSubRow = false, rowKey = "main") => (
    <Stack spacing={1}>
      {columns.map((col) => {
        if (!col) return null;

        const isActionColumn = col.type === "actions" && typeof col.getActions === "function";
        const fieldKey = col.field || "actions";
        const headerName = col.headerName || col.field || "Field";
        const isCombined = col.combine === true;
        const isFullWidth =
          typeof col.fullWidth === "function" ? col.fullWidth({ row: rowData }) : col.fullWidth === true;
        if (col.hide) {
          return null;
        }

        if (isSubRow && col.hideInSubRow) {
          return null;
        }

        if (isCombined) {
          if (isFullWidth) {
            return (
              <Stack key={fieldKey} direction="column" spacing={1} sx={{ width: "100%" }}>
                {isActionColumn
                  ? renderActionButtons(col.getActions({ row: rowData }), `${actionPopupId}-${rowKey}-${fieldKey}`)
                  : renderCellValue(col, rowData)}
              </Stack>
            );
          } else {
            return (
              <Stack key={fieldKey} direction="row">
                <Box
                  className={`mobile-datagrid-${fieldKey}`}
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    marginLeft: `${HEADER_WIDTH}px`,
                  }}
                >
                  {isActionColumn
                    ? renderActionButtons(col.getActions({ row: rowData }), `${actionPopupId}-${rowKey}-${fieldKey}`)
                    : renderCellValue(col, rowData)}
                </Box>
              </Stack>
            );
          }
        }

        return (
          <Stack key={fieldKey} direction="row" justifyContent="flex-start" alignItems={col.alignItems || "center"}>
            <Typography variant="subtitle2" width={HEADER_WIDTH} marginBottom={isActionColumn ? 0 : 2}>
              {headerName}
            </Typography>
            <Box
              className={`mobile-datagrid-${fieldKey}`}
              component="span"
              width={200}
              marginBottom={isActionColumn ? 0 : 2}
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              {isActionColumn
                ? renderActionButtons(col.getActions({ row: rowData }), `${actionPopupId}-${rowKey}-${fieldKey}`)
                : renderCellValue(col, rowData)}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );

  return (
    <Card key={row.id || `row-${index}`} className="responsive-data-grid-card" elevation={0} sx={getCardStyles(index)}>
      <CardContent className="responsive-data-grid-card-content">
        {renderRow(row)}

        {hasSubdata && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <ArrowDownIcon
              sx={{ width: 24, height: 24, transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
              onClick={handleExpandClick}
            />
          </Box>
        )}

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List
            component="div"
            disablePadding
            sx={{
              pl: 2,
              mt: 1,
              borderLeftWidth: "1px",
              borderLeftStyle: "solid",
              borderLeftColor: "divider",
            }}
          >
            {row.subdata?.map((subRow, subIndex) => (
              <ListItem key={subRow.id || `subrow-${subIndex}`} sx={{ display: "block", mb: 1 }}>
                {renderRow(subRow, true, subIndex)}
              </ListItem>
            ))}
          </List>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default function ResponsiveDataGrid({
  rows = [],
  columns = [],
  getRowId,
  renderCard,
  pageSizeOptions = [5, 10, 25, 50, 100],
  paginationModel = { page: 1, pageSize: 10 },
  onPaginationModelChange,
  sortModel = [],
  onSortModelChange,
  loading = false,
  error = null,
  emptyMessage = "No data available",
  isScrollToTopOnPageChange = false,
  hidePagination = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const gridContainerRef = useRef(null);
  const prevPaginationModelRef = useRef(paginationModel);
  const isInitialMountRef = useRef(true);

  // Constants
  const DEFAULT_PAGE_SIZE = 10;
  const DEFAULT_PAGE = 1;
  const HEADER_WIDTH = 130;

  const handleSortModelChange = useCallback(
    (newModel) => {
      if (typeof onSortModelChange === "function") {
        onSortModelChange(newModel);
      }
    },
    [onSortModelChange]
  );

  // Validation helpers
  const validateSortModel = useCallback(() => {
    if (sortModel?.[0]?.field && onSortModelChange) {
      const sortableColumns = columns.filter((c) => c && c.sortable !== false);
      const isValidSortField = sortableColumns.some((col) => col.field === sortModel[0].field);

      if (!isValidSortField) {
        onSortModelChange([]);
      }
    }
  }, [columns, sortModel, onSortModelChange]);

  // Data processing helpers
  const sortRows = useCallback((rowsToSort, sortConfig) => {
    if (!Array.isArray(rowsToSort) || !sortConfig?.field) return rowsToSort;

    try {
      const { field, sort } = sortConfig;
      return [...rowsToSort].sort((a, b) => {
        const aV = a?.[field] ?? "";
        const bV = b?.[field] ?? "";

        if (typeof aV === "number" && typeof bV === "number") {
          return sort === "asc" ? aV - bV : bV - aV;
        }

        const aStr = String(aV).toLowerCase();
        const bStr = String(bV).toLowerCase();

        if (aStr < bStr) return sort === "asc" ? -1 : 1;
        if (aStr > bStr) return sort === "asc" ? 1 : -1;
        return 0;
      });
    } catch (error) {
      console.warn("Error sorting rows:", error);
      return rowsToSort;
    }
  }, []);

  const paginateRows = useCallback((rowsToPaginate, pageConfig) => {
    if (!Array.isArray(rowsToPaginate)) return [];

    const pageSize = Math.max(1, pageConfig?.pageSize || DEFAULT_PAGE_SIZE);
    const page = Math.max(1, pageConfig?.page || DEFAULT_PAGE);
    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    if (start >= rowsToPaginate.length) return [];
    return rowsToPaginate.slice(start, end);
  }, []);

  // Processed data
  const sortedRows = useMemo(() => sortRows(rows, sortModel?.[0]), [rows, sortModel, sortRows]);
  const paginatedRows = useMemo(
    () => (hidePagination ? sortedRows : paginateRows(sortedRows, paginationModel)),
    [sortedRows, paginationModel, paginateRows, hidePagination]
  );

  useEffect(validateSortModel, [validateSortModel]);

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevPaginationModelRef.current = paginationModel;
      return;
    }

    const pageChanged =
      prevPaginationModelRef.current?.page !== paginationModel?.page ||
      prevPaginationModelRef.current?.pageSize !== paginationModel?.pageSize;

    if (isScrollToTopOnPageChange && isMobile && gridContainerRef.current && paginationModel && pageChanged) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (gridContainerRef.current) {
            const element = gridContainerRef.current;
            const elementTop = element.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
              top: elementTop - 20,
              behavior: "smooth",
            });
          }
        });
      });
    }

    prevPaginationModelRef.current = paginationModel;
  }, [isScrollToTopOnPageChange, isMobile, paginationModel?.page, paginationModel?.pageSize]);

  const extractActionProps = useCallback((actionElement) => {
    if (!actionElement?.props) return { icon: null, onClick: null, label: "" };

    const { children } = actionElement.props;

    if (children?.props?.children?.props) {
      return children.props.children.props;
    }

    if (children?.props) {
      return children.props;
    }

    return actionElement.props;
  }, []);

  const renderActionButtons = useCallback(
    (actionItems, popupId) => {
      if (!Array.isArray(actionItems)) return null;

      const renderActionButton = (actionElement, idx, childIdx = null) => {
        if (!actionElement?.props?.children) {
          const { icon, onClick, label } = extractActionProps(actionElement);
          return (
            <IconButton
              key={childIdx ?? idx}
              className="tilley-grid-action-button"
              sx={ACTION_BUTTON_SX}
              onClick={onClick}
              aria-label={label || "Action"}
              disabled={!onClick}
            >
              {icon}
            </IconButton>
          );
        }

        const actionElementChildren = actionElement.props.children;
        if (Array.isArray(actionElementChildren)) {
          return actionElementChildren.map((child, nestedIdx) => {
            if (!child?.props) return null;

            if (child.props.title === "Delete") {
              const { onChange, item } = child.props.children?.props || {};
              return (
                <DeleteConfirmationButton
                  key={`${idx}-${nestedIdx}`}
                  item={item}
                  onChange={onChange}
                  mobileDeleteAction={true}
                  className="tilley-grid-action-button"
                  sx={ACTION_BUTTON_SX}
                />
              );
            }

            const { icon, onClick, label } = extractActionProps(child);
            return (
              <IconButton
                key={`${idx}-${nestedIdx}`}
                className="tilley-grid-action-button"
                sx={ACTION_BUTTON_SX}
                onClick={onClick}
                aria-label={label || "Action"}
                disabled={!onClick}
              >
                {icon}
              </IconButton>
            );
          });
        }

        const { icon, onClick, label } = extractActionProps(actionElement);
        return (
          <IconButton
            key={idx}
            className="tilley-grid-action-button"
            sx={ACTION_BUTTON_SX}
            onClick={onClick}
            aria-label={label || "Action"}
            disabled={!onClick}
          >
            {icon}
          </IconButton>
        );
      };

      const inlineItems = actionItems.filter((el) => !el?.props?.showInMenu);
      const menuItems = actionItems.filter((el) => el?.props?.showInMenu);

      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          {inlineItems.map((actionElement, idx) => renderActionButton(actionElement, idx))}
          {menuItems.length > 0 && (
            <PopupState variant="popover" popupId={popupId}>
              {(popupState) => (
                <>
                  <IconButton
                    {...bindTrigger(popupState)}
                    className="tilley-grid-action-button"
                    sx={ACTION_BUTTON_SX}
                    aria-label="More actions"
                  >
                    <MoreVertOutlined />
                  </IconButton>
                  <Menu {...bindMenu(popupState)}>
                    {menuItems.map((actionElement, idx) => {
                      const { icon, onClick, label } = extractActionProps(actionElement);
                      const itemLabel = label || actionElement.props?.label || "Action";
                      const isDelete = /delete/i.test(itemLabel);
                      return (
                        <MenuItem
                          key={actionElement.key || idx}
                          disabled={!onClick}
                          divider={Boolean(actionElement.props?.divider)}
                          onClick={() => {
                            onClick?.();
                            popupState.close();
                          }}
                          sx={{
                            color: isDelete ? "error.main" : "text.secondary",
                          }}
                        >
                          {icon && (
                            <ListItemIcon sx={{ color: isDelete ? "error.main" : "text.secondary" }}>{icon}</ListItemIcon>
                          )}
                          {itemLabel}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </>
              )}
            </PopupState>
          )}
        </Stack>
      );
    },
    [extractActionProps]
  );

  const getColumnRawValue = useCallback((col, row) => {
    const fieldValue = row?.[col.field];
    if (!col.valueGetter) return fieldValue;

    // MUI DataGrid v6+ uses (value, row, column); older call sites used a single params object.
    if (col.valueGetter.length <= 1) {
      return col.valueGetter({ row, field: col.field, value: fieldValue });
    }
    return col.valueGetter(fieldValue, row, col);
  }, []);

  // Cell value rendering helper
  const renderCellValue = useCallback((col, row) => {
    try {
      const raw = getColumnRawValue(col, row);
      const value = raw ?? "-";

      let formattedValue = value;
      if (col.valueFormatter && value !== "-") {
        try {
          formattedValue = col.valueFormatter(value, row);
        } catch (formatterError) {
          console.warn("Error in valueFormatter:", formatterError);
          formattedValue = value;
        }
      }

      const display = col.renderCell
        ? col.renderCell({ row, value: formattedValue, field: col.field })
        : formattedValue;

      if (col.field === "producer_token") {
        const producerName = formatProducerName(row);
        return producerName || formattedValue || "-";
      }

      return display;
    } catch (error) {
      console.warn("Error rendering cell:", error);
      return (
        <Typography variant="body2" color="text.secondary">
          Error
        </Typography>
      );
    }
  }, [getColumnRawValue]);

  // Card styling helper
  const getCardStyles = useCallback((index) => {
    const isEven = index % 2 === 0;
    const backgroundColor = isEven ? "background.paper" : "grey.100";
    return {
      backgroundColor,
      marginTop: "0px !important",
      "&:hover": {
        backgroundColor: "grey.100",
        transition: "background-color 0.2s ease",
      },
    };
  }, []);

  const resolveRowId = useCallback(
    (row, index) => {
      if (typeof getRowId === "function") {
        try {
          return getRowId(row);
        } catch {
          // fall through
        }
      }
      return row?.id ?? `row-${index}`;
    },
    [getRowId]
  );

  // Default card renderer
  const defaultRenderCard = useCallback(
    (row = {}, index = 0) => {
      if (!Array.isArray(columns)) return null;
      const rowId = resolveRowId(row, index);
      return (
        <ResponsiveDataGridCard
          row={row}
          key={rowId}
          index={index}
          columns={columns}
          getCardStyles={getCardStyles}
          renderCellValue={renderCellValue}
          renderActionButtons={renderActionButtons}
        />
      );
    },
    [columns, getCardStyles, renderCellValue, renderActionButtons, resolveRowId]
  );

  // Skeleton card renderer
  const renderSkeletonCard = useCallback(
    (index = 0) => {
      if (!Array.isArray(columns)) return null;

      return (
        <Card className="responsive-data-grid-card skeleton-card" elevation={0} sx={getCardStyles(index)}>
          <CardContent className="responsive-data-grid-card-content">
            <Stack spacing={1}>
              {columns.map((col, colIndex) => {
                if (!col) return null;

                const fieldKey = col.field || `skeleton-${colIndex}`;
                const headerName = col.headerName || col.field || "Field";
                const isCombined = col.combine === true;

                if (isCombined) {
                  return null; // Skip skeleton for combined columns
                }

                return (
                  <Stack key={fieldKey} direction="row" justifyContent="flex-start" alignItems="center">
                    <Typography variant="subtitle2" width={HEADER_WIDTH} marginBottom={2}>
                      {headerName}
                    </Typography>
                    <Box component="span">
                      <Skeleton variant="text" width={100} height={20} />
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      );
    },
    [columns, getCardStyles]
  );

  // Controls component
  const Controls = useCallback(() => {
    const sortableColumns = columns.filter((c) => c && c.sortable !== false);

    if (sortableColumns.length === 0) {
      return null;
    }

    const currentSortField = sortModel?.[0]?.field || "";
    const isValidSortField = sortableColumns.some((col) => col.field === currentSortField);
    const safeSortField = isValidSortField ? currentSortField : "";
    const currentSortDirection = sortModel?.[0]?.sort || "asc";

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: 2,
          mb: 1,
        }}
      >
        <FormControl
          size="small"
          disabled={sortableColumns.length === 0}
          fullWidth
          sx={{ flex: { sm: "1 1 auto", xs: "1 1 100%" } }}
        >
          <InputLabel>Sort By</InputLabel>
          <Select
            value={safeSortField}
            label="Sort By"
            onChange={(e) => handleSortModelChange([{ field: e.target.value, sort: currentSortDirection }])}
          >
            <MenuItem value="">
              <em>Select a field</em>
            </MenuItem>
            {sortableColumns.map((c) => (
              <MenuItem key={c.field} value={c.field}>
                {c.headerName || c.field}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          disabled={!currentSortField}
          fullWidth
          sx={{ flex: { sm: "1 1 auto", xs: "1 1 100%" } }}
        >
          <InputLabel>Direction</InputLabel>
          <Select
            value={currentSortDirection}
            label="Direction"
            onChange={(e) =>
              handleSortModelChange([
                {
                  field: safeSortField || sortableColumns[0]?.field || "",
                  sort: e.target.value,
                },
              ])
            }
          >
            <MenuItem value="asc">Ascending</MenuItem>
            <MenuItem value="desc">Descending</MenuItem>
          </Select>
        </FormControl>
      </Box>
    );
  }, [columns, sortModel, paginationModel, handleSortModelChange]);

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {typeof error === "string" ? error : "An error occurred while loading data"}
      </Alert>
    );
  }

  // Empty state
  if (!loading && (!Array.isArray(rows) || rows.length === 0)) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  // Mobile layout: cards with controls and pagination
  if (isMobile) {
    const totalItems = sortedRows.length;
    let className = "responsive-data-grid-stack";
    if (totalItems === 1) {
      className = "responsive-data-grid-stack all-side-rounded";
    } else if (totalItems <= 5) {
      className = "responsive-data-grid-stack top-bottom-side-rounded";
    } else {
      className = "responsive-data-grid-stack top-side-rounded";
    }

    return (
      <Box ref={gridContainerRef} className="responsive-data-grid" mt={2} mb={2}>
        <Controls />
        <Stack spacing={2} className={className}>
          {loading
            ? [0].map((_, i) => <Box key={i}>{renderSkeletonCard(i)}</Box>)
            : paginatedRows.map((row, i) => (renderCard ? renderCard(row, columns) : defaultRenderCard(row, i)))}
        </Stack>

        {!hidePagination && !loading && totalItems >= 5 && (
          <PaginationControls
            totalItems={totalItems}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            pageSizeOptions={pageSizeOptions}
            loading={loading}
          />
        )}
      </Box>
    );
  }
}
